/* eslint-disable camelcase */
import omit from 'lodash/omit';

import { DataSourceTable } from 'src/constants/dataSourceTable';
import { DataSource } from 'src/store/state/dataSources';
import {
  METADATA_REGISTRY_DATA_SOURCE_NAME_IRI,
  METADATA_REGISTRY_DATA_SOURCE_TABLE_IRI,
  METADATA_REGISTRY_TABLE_CATALOG_IRI,
  METADATA_REGISTRY_TABLE_NAME_IRI,
  METADATA_REGISTRY_TABLE_SCHEMA_IRI,
  METADATA_REGISTRY_TABLE_TYPE_INDEX_IRI,
  METADATA_REGISTRY_TABLE_TYPE_IRI,
  METADATA_REGISTRY_TABLE_TYPE_SEQUENCE_IRI,
} from 'src/templates/queries/iris';

enum TableType {
  INDEX = 'INDEX',
  SEQUENCE = 'SEQUENCE',
}

export type TableMetadataJsonLdRow = Record<
  string,
  Array<Record<string, string>>
> & {
  '@id': string;
};

export type TableEntry = {
  catalog: string;
  schema: string;
  table_name: string;
  table_type: string;
};

// before Stardog 10.1.0
export const parseDataSourceTableMetadataResults = ({
  isCatalogSupported,
  prevDataSourcesById,
  tableMetadataJsonLdRows,
}: {
  isCatalogSupported: boolean;
  prevDataSourcesById: Record<string, DataSource> | null;
  tableMetadataJsonLdRows: TableMetadataJsonLdRow[];
}) => {
  const dataSourcesById: Record<string, DataSource> = {
    ...prevDataSourcesById,
  };

  interface TableToAdd extends Omit<DataSourceTable, 'name'> {
    dataSourceId: string;
    name: string | null;
  }
  const tablesToAdd: Record<string, TableToAdd> = {};
  tableMetadataJsonLdRows.forEach((row: TableMetadataJsonLdRow) => {
    row[METADATA_REGISTRY_DATA_SOURCE_TABLE_IRI]?.forEach((table) => {
      const dataSourceId =
        row[METADATA_REGISTRY_DATA_SOURCE_NAME_IRI]?.[0]?.['@value'];
      const dataSource = dataSourcesById[dataSourceId];
      if (dataSource && table['@id']) {
        tablesToAdd[table['@id']] = {
          dataSourceId,
          name: null,
        };
      }
    });
  });
  tableMetadataJsonLdRows.forEach((row) => {
    const tableId = row['@id'];
    const tableTypes = row[METADATA_REGISTRY_TABLE_TYPE_IRI];
    const isHiddenTable = tableTypes?.some(
      (tableType) =>
        tableType['@id'] === METADATA_REGISTRY_TABLE_TYPE_INDEX_IRI ||
        tableType['@id'] === METADATA_REGISTRY_TABLE_TYPE_SEQUENCE_IRI
    );
    const name = row[METADATA_REGISTRY_TABLE_NAME_IRI]?.[0]?.['@value'];
    const schema = row[METADATA_REGISTRY_TABLE_SCHEMA_IRI]?.[0]?.['@value'];
    const catalog = row[METADATA_REGISTRY_TABLE_CATALOG_IRI]?.[0]?.['@value'];
    if (tablesToAdd[tableId] && name !== undefined && !isHiddenTable) {
      tablesToAdd[row['@id']].name = name;
      if (schema) {
        tablesToAdd[row['@id']].schema = schema;
      }
      if (catalog && isCatalogSupported) {
        tablesToAdd[row['@id']].catalog = catalog;
      }
    }
  });

  Object.values(tablesToAdd).forEach((tableToAdd) => {
    if (tableToAdd.name !== null) {
      const dataSource = dataSourcesById[tableToAdd.dataSourceId];
      dataSourcesById[tableToAdd.dataSourceId] = {
        ...dataSource,
        tables: [
          ...(dataSource.tables || []),
          omit(tableToAdd, 'dataSourceId') as DataSourceTable,
        ],
        tableMetadataErrorResponse: null,
      };
    }
  });

  return dataSourcesById;
};

export const parseDataSourceTablesResults = ({
  dataSourceId,
  prevDataSourcesById,
  tableEntries,
}: {
  dataSourceId: string;
  prevDataSourcesById: Record<string, DataSource> | null;
  tableEntries: TableEntry[];
}) => {
  const dataSourcesById: Record<string, DataSource> = {
    ...prevDataSourcesById,
  };

  const tables: DataSourceTable[] = tableEntries
    .filter(
      ({ table_name, table_type }) =>
        table_name &&
        table_type !== TableType.INDEX &&
        table_type !== TableType.SEQUENCE
    )
    .map(({ catalog, schema, table_name }) => ({
      catalog,
      name: table_name,
      schema,
    }));

  dataSourcesById[dataSourceId] = {
    ...dataSourcesById[dataSourceId],
    tables,
    tableMetadataErrorResponse: null,
  };

  return dataSourcesById;
};
